import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset, useResetAtom } from 'jotai/utils'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIVASetChatStatus } from 'iva-react-web-ui'
import { triggerAnalyticsEvent } from '@syf-utils/analytics'
import {
  modalStatusAtom,
  pendingAccountServicesLaunchAtom,
  selectedAccountAtom
} from 'store/atoms'
import type { Modals } from 'store/atoms/typings'
import routes, { type Routes } from 'const/routes'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import {
  type GlobalSearch,
  SEARCH_METHOD_TEXT_INPUT,
  SEARCH_TYPE_GLOBAL,
  resetGlobalSearch,
  sanitizeSearchTerm,
  updateGlobalSearch
} from 'helpers/searchAnalytics'
import useSemanticSearch, {
  type ResolvedSearchItem
} from 'hooks/useSemanticSearch'
import {
  type EligibleAccount,
  useSearchEligibleAccounts
} from 'hooks/useAccountContexts'
import { numericBreaks } from 'syf-component-library/const/mediaQueries'
import { SearchContainer } from './subcomponents'
import SearchBar from './SearchBar'
import SearchDropdown from './SearchDropdown'

// ─── Local hooks ───────────────────────────────────────────────────────────
/** Stable handler identity that always invokes the latest closure. */
const useEventCallback = <A extends unknown[], R>(
  fn: (...args: A) => R
): ((...args: A) => R) => {
  const ref = useRef(fn)
  ref.current = fn
  return useCallback((...args: A) => ref.current(...args), [])
}

const useOutsideClick = <T extends HTMLElement>(
  ref: RefObject<T>,
  onOutside: (event: PointerEvent) => void
): void => {
  const handlerRef = useRef(onOutside)
  handlerRef.current = onOutside
  useEffect(() => {
    // pointerdown, not mousedown: iOS Safari skips mouse events for taps on non-clickable areas.
    const handlePointerDown = (event: PointerEvent) => {
      const el = ref.current
      if (el && !el.contains(event.target as Node)) handlerRef.current(event)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [ref])
}

// ─── State machine ─────────────────────────────────────────────────────────
export type SearchPhase = 'idle' | 'results' | 'accounts'

interface SearchState {
  query: string
  phase: SearchPhase
  results: ResolvedSearchItem[]
  selectedItem: ResolvedSearchItem | null
  activeIndex: number
}

const NO_ACTIVE_INDEX = -1
const DEBOUNCE_MS = 300
const normalizeSearchResultCategory = (category: string): string =>
  category.toLowerCase().replace(/\s/g, '')

const LANG_BUTTON_SELECTOR = '#language-button'
const MOBILE_PROFILE_SELECTOR = '#profile-container-system-navigation'

const initialSearchState: SearchState = {
  query: '',
  phase: 'idle',
  results: [],
  selectedItem: null,
  activeIndex: NO_ACTIVE_INDEX
}

const searchStateAtom = atomWithReset<SearchState>(initialSearchState)

/** Inserts the portal container before the first SystemNavigation anchor present in the DOM. */
const attachPortalContainer = (
  container: HTMLElement | null,
  setPortalMount: (el: HTMLElement) => void
): void => {
  if (container?.isConnected || !container) return

  // Desktop: before #language-button inside ProfileContent (flex child).
  const langBtn = document.querySelector(LANG_BUTTON_SELECTOR)
  if (langBtn?.parentElement) {
    langBtn.before(container)
    setPortalMount(container)
    return
  }

  // Mobile: before #profile-container-system-navigation .
  const mobileProfile = document.querySelector(MOBILE_PROFILE_SELECTOR)
  if (mobileProfile?.parentElement) {
    const mobilePortalContainer = container
    // Let SearchContainer shrink as a flex item of the mobile header row.
    mobilePortalContainer.style.display = 'contents'
    mobileProfile.before(mobilePortalContainer)
    setPortalMount(mobilePortalContainer)
  }
}

// ─── SFDDL_V2 analytics ────────────────────────────────────────────────────
/* Attributes shared across every searchbar SFDDL_V2 event. */
const SFDDL_V2_BASE = {
  capability: 'dashboard',
  feature: 'searchbar'
} as const

/** Where the last selected search result sent the user, so we know when they leave it. */
interface SearchDestination {
  route: string
  modal?: Modals
}

/* Legal (category, action) pairs — keeps analytics wiring compiler-checked. */
type SfddlV2Params =
  | { category: 'impression'; action: 'view' }
  | {
      category: 'interaction'
      action: 'start'
      searchedValue: string
      resultFound: boolean
    }
  | {
      category: 'result'
      action: 'success'
      searchedValue: string
      searchedResult: string
    }

const trackV2 = (params: SfddlV2Params) => {
  const event: Record<string, string> = {
    ...SFDDL_V2_BASE,
    category: params.category,
    action: params.action
  }
  if (params.category === 'interaction') {
    event.searched_value = sanitizeSearchTerm(params.searchedValue)
    event.result_found = String(params.resultFound)
  } else if (params.category === 'result') {
    event.searched_value = sanitizeSearchTerm(params.searchedValue)
    event.searched_result = params.searchedResult
  }

  triggerAnalyticsEvent(
    {},
    {
      correlationId: sessionStorage.getItem('correlationId') || '',
      event
    },
    { shouldPassthroughV1Trigger: true }
  )
}

/** Hidden while account data is loading/failed; otherwise portaled when a mount point exists. */
const renderSearch = (
  element: ReactElement,
  mount: HTMLElement | null,
  isLoadingOrError: boolean
): ReactElement | null => {
  if (isLoadingOrError) return null
  return mount ? createPortal(element, mount) : element
}

// ─── Component ─────────────────────────────────────────────────────────────
const Search = (): ReactElement | null => {
  const [state, setState] = useAtom(searchStateAtom)
  const [isExpanded, setIsExpanded] = useState(false)
  const resetState = useResetAtom(searchStateAtom)
  const { query, phase, results, selectedItem, activeIndex } = state

  const { search, isLoading, isError } = useSemanticSearch()
  const accounts = useSearchEligibleAccounts(selectedItem)

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const [{ modalId }, updateModalStatus] = useAtom(modalStatusAtom)
  const setPendingAccountServicesLaunch = useSetAtom(
    pendingAccountServicesLaunchAtom
  )
  const { pointOfNeedChat } = useIVASetChatStatus()

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const destinationRef = useRef<SearchDestination | null>(null)
  const destinationModalOpenedRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isOpen = phase !== 'idle'

  // ── Portal mount: render as a sibling of the SystemNavigation language button ──
  const [portalMount, setPortalMount] = useState<HTMLElement | null>(null)

  // useLayoutEffect (not useEffect) so the portal attaches before paint — prevents a flash of Search rendered in its fallback inline position.
  useLayoutEffect(() => {
    const desktopViewport = window.matchMedia(
      `(min-width: ${numericBreaks.medium}px)`
    )
    let container: HTMLElement | null = null
    let observer: MutationObserver | null = null

    const teardown = () => {
      observer?.disconnect()
      observer = null
      container?.remove()
      container = null
      setPortalMount(null)
    }

    const setup = () => {
      container = document.createElement('div')
      container.dataset.searchPortal = ''

      const attachContainer = () =>
        attachPortalContainer(container, setPortalMount)

      attachContainer()

      if (container?.isConnected) return

      // Startup race: anchor not in DOM yet. Observe until it appears, then disconnect.
      observer = new MutationObserver(() => {
        attachContainer()
        if (container?.isConnected) observer?.disconnect()
      })
      observer.observe(document.body, { childList: true, subtree: true })
    }

    const handleViewportChange = () => {
      teardown()
      setup()
    }

    setup()

    // Safari 13 and older don't have addEventListener on MediaQueryList — skip resize responsiveness.
    const supportsChangeListener =
      typeof desktopViewport.addEventListener === 'function'
    if (supportsChangeListener) {
      desktopViewport.addEventListener('change', handleViewportChange)
    }

    return () => {
      if (supportsChangeListener) {
        desktopViewport.removeEventListener('change', handleViewportChange)
      }
      teardown()
    }
  }, [])

  // ── SFDDL_V2 impression (fires once per mount; ref guards StrictMode + isLoading toggles) ──
  const impressionFiredRef = useRef(false)
  useEffect(() => {
    if (isLoading || isError || impressionFiredRef.current) return
    impressionFiredRef.current = true
    trackV2({ category: 'impression', action: 'view' })
  }, [isLoading, isError])

  // Drop the search analytics context once the user leaves the destination the search took them to.
  useEffect(() => {
    const destination = destinationRef.current
    if (!destination) return

    const clearDestination = () => {
      destinationRef.current = null
      destinationModalOpenedRef.current = false
      resetGlobalSearch()
    }

    if (pathname !== destination.route) {
      clearDestination()
      return
    }

    // Route-only destination: incidental modals (session-expiry, welcome interstitial, notification center) must not wipe the DDL. Only actual navigation away clears it.
    if (!destination.modal) return

    if (modalId === destination.modal) {
      destinationModalOpenedRef.current = true
    } else if (destinationModalOpenedRef.current) {
      // Searched modal was reached and has now closed or been replaced.
      clearDestination()
    }
  }, [pathname, modalId])

  // Abandoned-search cleanup: if the user never navigated anywhere, clear the DDL on unmount.
  useEffect(
    () => () => {
      if (!destinationRef.current) resetGlobalSearch()
    },
    []
  )

  // ── State transitions ──
  const setQuery = useEventCallback((searchedText: string) =>
    setState((prev) => ({
      ...prev,
      query: searchedText,
      results: [],
      selectedItem: null,
      phase: 'idle',
      activeIndex: NO_ACTIVE_INDEX
    }))
  )
  const openResults = useEventCallback(() => {
    // Cancel any pending debounced write so it cannot repopulate the DDL after the reset below.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    // Clear any leftover data from the previous search; selection paths write the DDL atomically.
    destinationRef.current = null
    destinationModalOpenedRef.current = false
    resetGlobalSearch()

    setState((prev) => (prev.query ? { ...prev, phase: 'results' } : prev))
  })

  const handleFocus = useEventCallback(() => {
    setIsExpanded(true)
    openResults()
  })

  const backToResults = useEventCallback(() =>
    setState((prev) => ({
      ...prev,
      selectedItem: null,
      phase: 'results',
      activeIndex: NO_ACTIVE_INDEX
    }))
  )
  const moveActive = useEventCallback((delta: number, max: number) =>
    setState((prev) => ({
      ...prev,
      activeIndex:
        delta > 0
          ? Math.min(prev.activeIndex + 1, max)
          : Math.max(prev.activeIndex - 1, 0)
    }))
  )

  /*
   * ── Navigation ──
   * Account Services opens its queued modal after loading the account; the dashboard opens it directly.
   */
  const launchModalOn = useEventCallback(
    (parentRoute: Routes, modal: Modals, triggerId: string) => {
      if (parentRoute === routes.HOME) {
        updateModalStatus(modal, triggerId)
      } else {
        setPendingAccountServicesLaunch({ type: 'modal', modal, triggerId })
      }
      navigate(parentRoute)
    }
  )

  const navigateToFeature = useEventCallback(
    (item: ResolvedSearchItem, accountId: string) => {
      if (accountId) setSelectedAccount(accountId)
      const { action, featureFlag, ivaFallback } = item
      const parentRoute =
        'parentRoute' in action ? action.parentRoute : undefined

      destinationModalOpenedRef.current = false

      if (featureFlag && !isFeatureFlagEnabled(featureFlag)) {
        // Flag off: filter would have hidden this item unless an IVA fallback exists.
        if (ivaFallback && parentRoute) {
          destinationRef.current = { route: parentRoute }
          setPendingAccountServicesLaunch({
            type: 'iva',
            pointOfNeed: ivaFallback,
            accountId
          })
          navigate(parentRoute)
        } else if (ivaFallback) {
          destinationRef.current = { route: pathname }
          pointOfNeedChat(ivaFallback, accountId)
        }
        return
      }

      if (action.type === 'iva') {
        destinationRef.current = { route: pathname }
        pointOfNeedChat(action.pointOfNeed, accountId)
      } else if (action.type === 'modal' && 'parentRoute' in action) {
        destinationRef.current = {
          route: action.parentRoute,
          modal: action.modal
        }
        launchModalOn(
          action.parentRoute,
          action.modal,
          action.getTriggerId(accountId)
        )
      } else if (action.type === 'modal') {
        destinationRef.current = { route: pathname, modal: action.modal }
        const triggerId =
          'getTriggerId' in action ? action.getTriggerId(accountId) : ''
        updateModalStatus(action.modal, triggerId)
      } else {
        destinationRef.current = { route: action.to }
        navigate(action.to)
      }
    }
  )

  // ── Debounced search ──
  useEffect(() => {
    if (!query.trim() || isLoading || isError) {
      setState((prev) =>
        prev.results.length === 0 ? prev : { ...prev, results: [] }
      )
      return undefined
    }
    const timer = setTimeout(() => {
      debounceTimerRef.current = null
      const matches = search(query)
      setState((prev) => ({
        ...prev,
        results: matches,
        phase: 'results',
        activeIndex: NO_ACTIVE_INDEX
      }))
      trackV2({
        category: 'interaction',
        action: 'start',
        searchedValue: query,
        resultFound: matches.length > 0
      })
    }, DEBOUNCE_MS)
    debounceTimerRef.current = timer
    return () => {
      clearTimeout(timer)
      if (debounceTimerRef.current === timer) debounceTimerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isLoading, isError])

  // ── Close / dismiss ──
  const closeSearch = useEventCallback(() => {
    setIsExpanded(false)
    resetState()
  })

  const handleContainerBlur = useEventCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (containerRef.current?.contains(e.relatedTarget as Node | null)) return
      closeSearch()
    }
  )

  const closeAndBlur = useEventCallback(() => {
    closeSearch()
    inputRef.current?.blur()
  })

  // A tap outside may not blur the input on iOS, which would leave the keyboard open.
  useOutsideClick(containerRef, () => closeAndBlur())

  // Safari doesn't focus clicked buttons, so blur would close Search before the click lands. The input itself must stay clickable.
  const keepFocusInside = useEventCallback((e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== inputRef.current) e.preventDefault()
  })

  // ── Feature selection ──
  const selectFeature = useEventCallback(
    (item: ResolvedSearchItem, accountId: string) => {
      // searched_result uses item.id (stable slug) rather than item.label (localised).
      trackV2({
        category: 'result',
        action: 'success',
        searchedValue: query,
        searchedResult: item.id
      })

      // Commit the full DDL atomically so downstream analytics see one complete globalSearch object.
      const searchAttrs: GlobalSearch = {
        searchTerm: sanitizeSearchTerm(query),
        searchMethod: SEARCH_METHOD_TEXT_INPUT,
        searchType: SEARCH_TYPE_GLOBAL,
        searchResultCategory: normalizeSearchResultCategory(item.category),
        searchResult: item.id,
        accountSelectionRequired: item.isAccountScoped,
        accountSelected: item.isAccountScoped && accounts.length > 1
      }
      updateGlobalSearch(searchAttrs)
      const action =
        accounts.find((account) => account.accountId === accountId)?.action ??
        item.action
      navigateToFeature({ ...item, action }, accountId)
      closeAndBlur()
    }
  )

  const handleSelectItem = useEventCallback((item: ResolvedSearchItem) => {
    // Site-wide destinations have no account to disambiguate.
    if (!item.isAccountScoped) {
      selectFeature(item, '')
      return
    }
    setState((prev) => ({
      ...prev,
      selectedItem: item,
      phase: 'accounts',
      activeIndex: NO_ACTIVE_INDEX
    }))
  })

  const handleSelectAccount = useEventCallback((account: EligibleAccount) => {
    if (!selectedItem) return
    selectFeature(selectedItem, account.accountId)
  })

  // Auto-navigate when only one account is eligible.
  useEffect(() => {
    if (phase !== 'accounts' || !selectedItem) return
    if (accounts.length === 1) {
      selectFeature(selectedItem, accounts[0].accountId)
    }
  }, [selectedItem, accounts, selectFeature, phase])

  // ── Keyboard ──
  const getAccountOptions = () =>
    Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ??
        []
    )

  // The picker moves real focus onto account buttons, so focus (not activeIndex) drives it; Enter uses the focused button's native click.
  const handleAccountKeyDown = useEventCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const options = getAccountOptions()
      const current = options.indexOf(document.activeElement as HTMLElement)
      const next =
        e.key === 'ArrowDown'
          ? Math.min(current + 1, options.length - 1)
          : Math.max(current - 1, 0)
      options[next]?.focus()
    } else if (e.key === 'Backspace' && e.target !== inputRef.current) {
      e.preventDefault()
      backToResults()
      inputRef.current?.focus()
    }
  })

  const syncActiveWithFocus = useEventCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (phase !== 'accounts') return
      const index = getAccountOptions().indexOf(e.target as HTMLElement)
      if (index >= 0) setState((prev) => ({ ...prev, activeIndex: index }))
    }
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAndBlur()
        return
      }
      if (phase === 'accounts') {
        handleAccountKeyDown(e)
        return
      }
      // Buttons reached with Tab (e.g. clear) must keep their own Enter/click behaviour.
      if (phase !== 'results' || e.target !== inputRef.current) return

      const max = results.length - 1

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          moveActive(1, max)
          break
        case 'ArrowUp':
          e.preventDefault()
          moveActive(-1, max)
          break
        case 'Enter':
          if (activeIndex < 0) break
          e.preventDefault()
          handleSelectItem(results[activeIndex])
          break
        default:
          break
      }
    },
    [
      phase,
      results,
      activeIndex,
      moveActive,
      handleSelectItem,
      handleAccountKeyDown,
      closeAndBlur
    ]
  )

  const searchElement = (
    <SearchContainer
      ref={containerRef}
      $isExpanded={isExpanded}
      $isPortaled={!!portalMount}
      data-expanded={isExpanded}
      data-test="search-container"
      onBlur={handleContainerBlur}
      onFocus={syncActiveWithFocus}
      onKeyDown={handleKeyDown}
      onMouseDown={keepFocusInside}
    >
      <SearchBar
        ref={inputRef}
        value={query}
        onChange={setQuery}
        onFocus={handleFocus}
        isOpen={isOpen}
        isExpanded={isExpanded}
      />
      {isOpen && query && (
        <SearchDropdown
          state={state}
          accounts={accounts}
          onSelectItem={handleSelectItem}
          onSelectAccount={handleSelectAccount}
        />
      )}
    </SearchContainer>
  )

  return renderSearch(searchElement, portalMount, isLoading || isError)
}

export default Search
